import * as React from 'react';
import { Store } from '../stores';
import { inject, observer } from 'mobx-react';
import { IAPanel } from '../../panels/Panel';
import InstanceService from '../../../services/InstanceService';
import { IInstance } from '../../../interfaces/IInstance';
import { IATextField } from '../../textField/TextField';
import { LocalizationService } from '../../../services/LocalizationService';
import { IASkeleton } from '../../skeleton/Skeleton';
import { IACheckbox } from '../../checkbox/Checkbox';
import { IAButton } from '../../button/Button';
import FeedService from '../../../services/FeedService';
import { IFeed } from '../../../interfaces/IFeed';

export interface IProps {
  store?: Store;
  onClosePanel: () => void;
  onFeedUpdatedOrCreated: () => void;
}

export interface IState {
  feedToEditCopy: IFeed;
  isLoadingInstances: boolean;
  instances: {id: string, title: string}[];
  isCreatingOrUpdatingFeed: boolean;
  errorWhileUpdatingOrCreatingFeed: boolean;
}

@inject('store')
@observer
export class FeedCreatorPanel extends React.Component<IProps, IState> {
  private readonly localizationService: LocalizationService = new LocalizationService();
  
  constructor(props: IProps) {
    super(props);
    this.state = {
      feedToEditCopy: undefined,
      instances: undefined,
      isLoadingInstances: false,
      isCreatingOrUpdatingFeed: false,
      errorWhileUpdatingOrCreatingFeed: false
    }
  }

  componentDidUpdate(): void {
    if (!!this.props.store.feedToEdit && this.state.instances === undefined && !this.state.isLoadingInstances) {
      this.setState({isLoadingInstances: true, feedToEditCopy: JSON.parse(JSON.stringify(this.props.store.feedToEdit))});
      let component: "IntraActive-Messages" | "IntraActive-Events";
      if (this.props.store.selectedApplication === "messages") {
        component = "IntraActive-Messages";
      }
      if (this.props.store.selectedApplication === "events") {
        component = "IntraActive-Events";
      }
      InstanceService.getInstances(this.props.store.environment, this.props.store.token, this.props.store.tenant, component, true)
        .then((instances: IInstance[]) => {
          this.setState({instances: instances.map(instance => {
            return {
              id: instance.instanceName ?? "Default",
              title: instance.title,
            }
          })});
        })
        .catch(() => this.setState({instances: []}))
        .finally(() => this.setState({isLoadingInstances: false}));
    }
  }

  private closePanel() {
    if (!this.state.isCreatingOrUpdatingFeed) {
      this.setState({instances: undefined});
      this.props.onClosePanel();
    }
  }

  private onFeedUpdatedOrCreated() {
    this.setState({isCreatingOrUpdatingFeed: false}, () => {
      this.closePanel();
      this.props.onFeedUpdatedOrCreated();
    });
  }

  public render(): JSX.Element {
    return <IAPanel
      close={() => this.closePanel()}
      open={!!this.props.store.feedToEdit}
      showNavigationBar
      transition="slide"
      width={600}
      footer={
        <IAButton
          label={this.state.feedToEditCopy?.id ? this.localizationService.strings.PlayAdmin_Update : this.localizationService.strings.Admin_Add}
          icon="Save"
          iconColor={"#ffffff"}
          textColor={"#ffffff"}
          borderRadius={5}
          disbaled={this.state.isLoadingInstances || !this.state.feedToEditCopy?.title || !(this.state.feedToEditCopy?.instances?.length > 0)}
          buttonColor={this.props.store.highlightColor}
          showSpinner={this.state.isCreatingOrUpdatingFeed}
          onClick={() => {
            this.setState({isCreatingOrUpdatingFeed: true});
            if (!this.state.feedToEditCopy.id) {
              // if there is no ID, we are creating a new feed
              FeedService.createFeed(this.props.store.environment, this.props.store.tenant, this.props.store.token, this.state.feedToEditCopy)
                .then(() => this.onFeedUpdatedOrCreated())
                .catch(() => this.setState({isCreatingOrUpdatingFeed: false, errorWhileUpdatingOrCreatingFeed: true}));
            } else {
              FeedService.updateFeed(this.props.store.environment, this.props.store.tenant, this.props.store.token, this.state.feedToEditCopy)
                .then(() => this.onFeedUpdatedOrCreated())
                .catch(() => this.setState({isCreatingOrUpdatingFeed: false, errorWhileUpdatingOrCreatingFeed: true}));
            }
          }}
          style={{
            marginTop: 10,
            marginBottom: 10
          }}
        />
      }
    >
      {!!this.state.feedToEditCopy &&
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "0 40px"
          }}
        >
          <IATextField
            label={this.localizationService.strings.TargetingAdmin_UserGroup_EditPanel_Title}
            text={this.state.feedToEditCopy?.title}
            borderRadius={5}
            placeholder={this.localizationService.strings.TargetingAdmin_UserGroup_EditPanel_Title}
            highlightColor={this.props.store.highlightColor}
            onChange={(newTitle: string) => {
              this.state.feedToEditCopy.title = newTitle;
            }}
            disabled={this.state.isLoadingInstances || this.state.isCreatingOrUpdatingFeed}
          />
          <label style={{
            marginTop: 20,
            marginBottom: 10
          }}>
            {this.localizationService.strings.PlayAdmin_SelectInstances}
          </label>
          {this.state.isLoadingInstances ?
            <>
              {Array(5).fill(null).map(() => {
                return <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: 9
                  }}
                >
                  <IASkeleton
                    skeleton={{
                      height: 20,
                      width: 20,
                      borderRadius: 2,
                      marginRight: 5
                    }}
                  />
                  <IASkeleton
                    skeleton={{
                      height: 20,
                      width: 120,
                      borderRadius: 5,
                    }}
                  />
                </div>
              })}
            </>
            :
            <>
              {this.state.instances?.map(instance => {
                return <IACheckbox
                  key={instance.id}
                  label={instance.title}
                  highlightColor={this.props.store.highlightColor}
                  disabled={(this.state.feedToEditCopy.instances.indexOf(instance?.id) === -1 && this.state.feedToEditCopy.instances?.length === 3) || this.state.isCreatingOrUpdatingFeed}
                  isChecked={this.state.feedToEditCopy.instances.indexOf(instance?.id) !== -1}
                  onChange={() => {
                    const index = this.state.feedToEditCopy.instances.indexOf(instance?.id);
                    if (index === -1) {
                      this.state.feedToEditCopy.instances.push(instance?.id);
                    } else {
                      this.state.feedToEditCopy.instances.splice(index, 1);
                    }
                    this.forceUpdate();
                  }}
                  styles={{
                    marginBottom: 10
                  }}
                />
              })}
            </>
          }
        </div>
      }
    </IAPanel>
  }
}